
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigChangeType} from './ConfigChangeType';
import {FieldModelConfigChangeType} from './ConfigChangeType';
import {ConfigValue} from './ConfigValue';
import {FieldModelConfigValue} from './ConfigValue';

/**
 * ConfigChange struct
 */
class ConfigChange {
  /**
   * Initialize struct
   * @param {!string=} key
   * @param {!ConfigChangeType=} changeType
   * @param {ConfigValue=} previous
   * @param {ConfigValue=} current
   * @constructor
   */
  constructor (key = '', changeType = new ConfigChangeType(), previous = null, current = null) {
    this.Key = key
    this.ChangeType = changeType
    this.Previous = previous
    this.Current = current
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigChange}
   * @param {!ConfigChange} other Other struct
   * @returns {!ConfigChange} This struct
   */
  copy (other) {
    if (other.Key != null) {
      this.Key = other.Key
    } else {
      this.Key = null
    }
    if (other.ChangeType != null) {
      this.ChangeType = ConfigChangeType.fromObject(other.ChangeType)
    } else {
      this.ChangeType = null
    }
    if (other.Previous != null) {
      this.Previous = ConfigValue.fromObject(other.Previous)
    } else {
      this.Previous = null
    }
    if (other.Current != null) {
      this.Current = ConfigValue.fromObject(other.Current)
    } else {
      this.Current = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigChange}
   * @returns {!ConfigChange} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigChangeModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigChangeModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigChange}
   * @param {!ConfigChange} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigChange)) {
      throw new TypeError('Instance of ConfigChange is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigChange}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Key: ((this.Key != null) ? this.Key : null),
      ChangeType: ((this.ChangeType != null) ? this.ChangeType : null),
      Previous: ((this.Previous != null) ? this.Previous : null),
      Current: ((this.Current != null) ? this.Current : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigChange.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigChange} other Object value
   * @returns {!ConfigChange} Created struct
   */
  static fromObject (other) {
    return new ConfigChange().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigChange}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigChange.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigChange}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1010
  }
}

export { ConfigChange };

/**
 * Fast Binary Encoding ConfigChange field model
 */
class FieldModelConfigChange extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Key = new fbe.FieldModelString(buffer, 4 + 4)
    this.ChangeType = new FieldModelConfigChangeType(buffer, this.Key.fbeOffset + this.Key.fbeSize)
    this.Previous = new fbe.FieldModelOptional(new FieldModelConfigValue(buffer, this.ChangeType.fbeOffset + this.ChangeType.fbeSize), buffer, this.ChangeType.fbeOffset + this.ChangeType.fbeSize)
    this.Current = new fbe.FieldModelOptional(new FieldModelConfigValue(buffer, this.Previous.fbeOffset + this.Previous.fbeSize), buffer, this.Previous.fbeOffset + this.Previous.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Key.fbeSize + this.ChangeType.fbeSize + this.Previous.fbeSize + this.Current.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Key.fbeExtra + this.ChangeType.fbeExtra + this.Previous.fbeExtra + this.Current.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigChange.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1010
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigChange}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigChange}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Key.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Key.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.ChangeType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChangeType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChangeType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Previous.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Current.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigChange}
   * @param {!ConfigChange} fbeValue Default value, defaults is new ConfigChange()
   * @returns {!ConfigChange} ConfigChange value
   */
  get (fbeValue = new ConfigChange()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigChange}
   * @param {!ConfigChange} fbeValue ConfigChange value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Key.fbeSize) <= fbeStructSize) {
      fbeValue.Key = this.Key.get()
    } else {
      fbeValue.Key = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.ChangeType.fbeSize) <= fbeStructSize) {
      fbeValue.ChangeType = this.ChangeType.get()
    } else {
      fbeValue.ChangeType = new ConfigChangeType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChangeType.fbeSize

    if ((fbeCurrentSize + this.Previous.fbeSize) <= fbeStructSize) {
      fbeValue.Previous = this.Previous.get()
    } else {
      fbeValue.Previous = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Previous.fbeSize

    if ((fbeCurrentSize + this.Current.fbeSize) <= fbeStructSize) {
      fbeValue.Current = this.Current.get()
    } else {
      fbeValue.Current = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Current.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigChange}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigChange}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigChange}
   * @param {!ConfigChange} fbeValue ConfigChange value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigChange}
   * @param {ConfigChange} fbeValue ConfigChange value
   */
  setFields (fbeValue) {
    this.Key.set(fbeValue.Key)
    this.ChangeType.set(fbeValue.ChangeType)
    this.Previous.set(fbeValue.Previous)
    this.Current.set(fbeValue.Current)
  }
}

export { FieldModelConfigChange };

/**
 * Fast Binary Encoding ConfigChange model
 */
class ConfigChangeModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigChange(this.buffer, 4)
  }

  /**
   * Get the ConfigChange model
   * @this {!ConfigChangeModel}
   * @returns {!FieldModelConfigChange} model ConfigChange model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigChangeModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigChangeModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigChangeModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigChangeModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigChange.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigChangeModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigChangeModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigChangeModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigChangeModel}
   * @param {!ConfigChange} value ConfigChange value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigChangeModel}
   * @param {!ConfigChange} value ConfigChange value, defaults is new ConfigChange()
   * @return {!object} Deserialized ConfigChange value and its size
   */
  deserialize (value = new ConfigChange()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigChange(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigChange(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigChangeModel}
   * @param {!number} prev Previous ConfigChange model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigChangeModel };
